import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useInvoices, useCreateInvoice, useDeleteInvoice } from '@/hooks/usePSI';
import { useInvoices as useFirestoreInvoices, useDeleteInvoice as useDeleteFirestoreInvoice } from '@/hooks/useInvoice';
import { IInvoice, IInvoiceCreateRequest } from '@/types/psi';
import type { Invoice } from '@/types/invoice';
import SearchBar from '@/components/SearchBar';
import Pagination from '@/components/Pagination';
import LoadingSpinner from '@/components/LoadingSpinner';
import { MobileCard } from '@/components/ui';
import InvoiceDialog from './InvoiceDialog';

type InvoiceSubTab = 'firestore' | 'sheet';

const InvoicesList: React.FC = () => {
  const { t } = useTranslation('psi');
  const [subTab, setSubTab] = useState<InvoiceSubTab>('firestore');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [newInvoice, setNewInvoice] = useState<Partial<IInvoiceCreateRequest>>({
    date: new Date().toISOString().split('T')[0],
    status: 'draft',
  });

  const { data, isLoading, refetch } = useInvoices({ page, pageSize, q: searchQuery });
  const createMutation = useCreateInvoice();
  const deleteMutation = useDeleteInvoice();

  // Firestore invoices
  const { data: firestoreData, isLoading: firestoreLoading } = useFirestoreInvoices({ limit: 50 });
  const deleteFirestoreMutation = useDeleteFirestoreInvoice();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleAddNew = async () => {
    if (!newInvoice.customer || !newInvoice.items) {
      alert('Customer and Items are required');
      return;
    }

    try {
      await createMutation.mutateAsync(newInvoice as IInvoiceCreateRequest);
      setIsAddingNew(false);
      setNewInvoice({ date: new Date().toISOString().split('T')[0], status: 'draft' });
    } catch (error) {
      logger.error('Failed to create invoice:', error);
    }
  };

  const handleDelete = async (invoiceNo: string) => {
    if (confirm('Are you sure you want to delete this invoice?')) {
      try {
        await deleteMutation.mutateAsync(invoiceNo);
      } catch (error) {
        logger.error('Failed to delete invoice:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 dark:text-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const renderFirestoreView = () => {
    if (firestoreLoading) return <LoadingSpinner />;

    const invoices = firestoreData?.invoices || [];
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <button
            onClick={() => setShowDialog(true)}
            className="px-3 sm:px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 min-h-[44px]"
          >
            + {t('invoice.dialog.createTitle', 'Create Invoice')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Counterparty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Grand Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {invoices.map((inv: Invoice) => (
                <tr key={inv.invoice_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm font-mono font-medium text-gray-900 dark:text-white">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${inv.invoice_type === 'purchase' ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700'}`}>
                      {inv.invoice_type === 'purchase' ? 'AP' : 'AR'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{inv.counterparty_name || inv.vendor_code || inv.customer_code || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{inv.currency} {inv.grand_total?.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { if (confirm('Delete this invoice?')) deleteFirestoreMutation.mutate(inv.invoice_id); }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No Firestore invoices found. Click "Create Invoice" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <InvoiceDialog isOpen={showDialog} onClose={() => setShowDialog(false)} />
      </div>
    );
  };

  if (subTab === 'firestore') {
    return (
      <div className="space-y-4">
        {/* Sub-tabs */}
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          <button
            onClick={() => setSubTab('firestore')}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white"
          >
            {t('invoice.subtabs.firestore', 'Firestore Invoices')}
          </button>
          <button
            onClick={() => setSubTab('sheet')}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {t('invoice.subtabs.sheet', 'Sheet Records')}
          </button>
        </div>
        {renderFirestoreView()}
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
        <button
          onClick={() => setSubTab('firestore')}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          {t('invoice.subtabs.firestore', 'Firestore Invoices')}
        </button>
        <button
          onClick={() => setSubTab('sheet')}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white"
        >
          {t('invoice.subtabs.sheet', 'Sheet Records')}
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-stretch sm:items-center">
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Search invoices..."
        />
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
          >
            Refresh
          </button>
          <button
            onClick={() => setIsAddingNew(true)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 min-h-[44px]"
          >
            + Add Invoice
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {data?.invoices?.map((invoice: IInvoice) => (
          <MobileCard
            key={invoice.invoice_no}
            title={<span className="font-mono">{invoice.invoice_no}</span>}
            subtitle={invoice.customer}
            status={
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                {invoice.status}
              </span>
            }
            fields={[
              { label: 'Date', value: invoice.date },
              { label: 'Due Date', value: invoice.due_date || '-' },
              { label: 'Items', value: invoice.items },
              { label: 'Total', value: `$${invoice.total.toFixed(2)}`, highlight: true },
            ]}
            actions={[
              { label: 'Delete', onClick: () => handleDelete(invoice.invoice_no), variant: 'danger' as const },
            ]}
          />
        ))}
        {(!data?.invoices || data.invoices.length === 0) && !isAddingNew && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No invoices found. Click "+ Add Invoice" to create one.
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Invoice #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Items</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Subtotal</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tax</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Due Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
            {isAddingNew && (
              <tr className="bg-green-50">
                <td className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">Auto</td>
                <td className="px-4 py-2">
                  <input
                    type="date"
                    value={newInvoice.date || ''}
                    onChange={(e) => setNewInvoice({ ...newInvoice, date: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="text"
                    placeholder="Customer"
                    value={newInvoice.customer || ''}
                    onChange={(e) => setNewInvoice({ ...newInvoice, customer: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="text"
                    placeholder="Items"
                    value={newInvoice.items || ''}
                    onChange={(e) => setNewInvoice({ ...newInvoice, items: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Subtotal"
                    value={newInvoice.subtotal || ''}
                    onChange={(e) => setNewInvoice({ ...newInvoice, subtotal: parseFloat(e.target.value) || 0 })}
                    className="w-24 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Tax"
                    value={newInvoice.tax || ''}
                    onChange={(e) => setNewInvoice({ ...newInvoice, tax: parseFloat(e.target.value) || 0 })}
                    className="w-20 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                  ${((newInvoice.subtotal || 0) + (newInvoice.tax || 0)).toFixed(2)}
                </td>
                <td className="px-4 py-2">
                  <select
                    value={newInvoice.status || 'draft'}
                    onChange={(e) => setNewInvoice({ ...newInvoice, status: e.target.value })}
                    className="px-2 py-1 text-sm border rounded"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="px-4 py-2">
                  <input
                    type="date"
                    value={newInvoice.due_date || ''}
                    onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={handleAddNew}
                    disabled={createMutation.isLoading}
                    className="text-green-600 hover:text-green-800 mr-2"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsAddingNew(false)}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200"
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            )}
            {data?.invoices?.map((invoice: IInvoice) => (
              <tr key={invoice.invoice_no} className="hover:bg-gray-50 dark:bg-gray-700">
                <td className="px-4 py-3 text-sm font-mono font-medium text-gray-900 dark:text-white">{invoice.invoice_no}</td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{invoice.date}</td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{invoice.customer}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={invoice.items}>
                  {invoice.items}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${invoice.subtotal.toFixed(2)}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">${invoice.tax.toFixed(2)}</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">${invoice.total.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{invoice.due_date || '-'}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleDelete(invoice.invoice_no)}
                    disabled={deleteMutation.isLoading}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!data?.invoices || data.invoices.length === 0) && !isAddingNew && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No invoices found. Click "+ Add Invoice" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={data.total_pages}
          onPageChange={setPage}
        />
      )}

      {/* Summary */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Showing {data?.invoices?.length || 0} of {data?.total_count || 0} invoices
      </div>
    </div>
  );
};

export default InvoicesList;
